/**
 * @voltxampere/engine — Transient gmin-stepping rescue (Sprint 54.3g
 * engine extension #8).
 *
 * Sprint 54.3f shipped {@link transientSourceStepping} as the per-step
 * Newton-fail rescue mechanism. Diagnosis on the fathers circuit
 * (54.3f-1) revealed that source-amplitude stiffness is NOT the
 * conditioning problem in that fixture — at h_min ≈ 1e-18, V_mains has
 * essentially zero amplitude regardless of α, so the source-stepping
 * sweep changes nothing. The actual problem is **Y matrix
 * ill-conditioning**: the LM393 open-collector 1pS HIGH-Z release,
 * the TRIAC/MOC state-pinning identity rows, and the active devices'
 * Jacobian sub-blocks combine into near-singular sub-systems.
 *
 * Gmin-stepping is the textbook fix for that. The DC fallback
 * (Sprint 5, SPECS §3.6, `dc/gmin-stepping.ts`) adds a regularising
 * conductance `gmin` between every node and ground, sweeping log-decade
 * from large (~1e-1, almost short-to-ground — well-conditioned) down
 * to small (1e-12) and finally to zero. Each level warm-starts from
 * the previous level's converged iterate. This module ports that
 * pattern into the transient solver's per-step Newton-fail rescue.
 *
 * Per-step rescue ordering (Sprint 54.3g):
 *
 *   plain Newton → gmin-stepping → source-stepping → h-halving ladder
 *
 * Rationale: gmin addresses conditioning (the 54.3f-1 root cause),
 * source-stepping addresses amplitude stiffness. For circuits where
 * the latter is the issue, gmin-stepping converges almost immediately
 * (the regularisation is unnecessary but harmless). For circuits where
 * conditioning is the issue, gmin-stepping converges where
 * source-stepping cannot.
 *
 * Engine baseline note: Sprint 54.3g resets the engine baseline tag
 * from `sprint-54-3f-baseline` to `sprint-54-3g-baseline`. This is
 * the 4th engine break of the Sprint 54 marathon.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError } from "@voltxampere/shared";
import { newtonRaphsonTransient } from "./newton-raphson.js";
/**
 * Continuation schedule for transient gmin-stepping. Mirrors the DC
 * gmin-stepping schedule (12 log-decade levels from 1e-1 to 1e-12)
 * but adds a final `0` level so the result is the un-regularised
 * Newton solve (consistent with DC's two-phase "schedule then final
 * gmin=0 verify" pattern).
 */
export const TRANSIENT_GMIN_STEPPING_SCHEDULE = [
    1e-1, 1e-2, 1e-3, 1e-4, 1e-5, 1e-6, 1e-7, 1e-8, 1e-9, 1e-10, 1e-11, 1e-12, 0,
];
/**
 * Run the gmin-stepping continuation for a single transient
 * time-step. Returns the result of the final (gmin = 0) solve, plus
 * accumulated iteration / clamp counters.
 *
 * Throws {@link ConvergenceError} if any continuation level fails to
 * converge. The caller is expected to interpret this as "even
 * gmin-stepping cannot rescue this step" and fall through to
 * source-stepping (Sprint 54.3f ext #7) or h-halving.
 */
export function transientGminStepping(circuit, plan, baseOpts, config) {
    const schedule = config.schedule ?? TRANSIENT_GMIN_STEPPING_SCHEDULE;
    let x = new Float64Array(baseOpts.x0);
    let totalIterations = 0;
    let totalClampEvents = 0;
    const stepsConverged = [];
    let lastResult;
    for (const gmin of schedule) {
        const r = newtonRaphsonTransient(circuit, plan, {
            ...baseOpts,
            maxIterations: config.maxIterations,
            x0: x,
            gmin,
        });
        totalIterations += r.iterations;
        totalClampEvents += r.clampEvents;
        if (!r.converged) {
            throw new ConvergenceError(`transient gmin stepping failed at gmin=${String(gmin)} after ${String(r.iterations)} iterations`, {
                meta: {
                    gmin,
                    iterations: totalIterations,
                    stepsConverged: [...stepsConverged],
                    fallback: "transient-gmin",
                },
            });
        }
        stepsConverged.push(gmin);
        x = r.x;
        lastResult = r;
    }
    if (lastResult === undefined) {
        // Empty schedule. Treat as success with the cold-start x0.
        return {
            result: {
                converged: true,
                iterations: 0,
                x,
                clampEvents: 0,
                finalResidualNorm: 0,
            },
            totalIterations: 0,
            totalClampEvents: 0,
            stepsConverged: [],
        };
    }
    return {
        result: lastResult,
        totalIterations,
        totalClampEvents,
        stepsConverged,
    };
}
//# sourceMappingURL=gmin-stepper.js.map